import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	setInEditorTemplatesPanelQueryParam,
	IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
	CONTEXT_PANEL_TAB_TARGET_BLOCK,
} from '@confluence/template-utils/entry-points/urlUtils.ts';

import { ANALYTICS_ORIGIN, ANALYTICS_UI_SUBJECT_ID } from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/useSnippetAnalytics.ts';

import FirstSnippetCreatedIllustration from './img/FirstSnippetCreatedIllustration.svg';
import { messages } from './FirstSnippetCreatedModal.i18n.ts';

type FirstSnippetCreatedModalProps = DialogsContainerProps;

const styles = cssMap({
	headerContainer: {
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingLeft: token('space.400'),
	},
	illustrationWrapper: {
		display: 'flex',
		justifyContent: 'center',
		paddingBottom: token('space.100'),
	},
});

const FIRST_SNIPPET_CREATED_ANALYTICS = {
	ACTION_VIEWED: 'viewed',
	ACTION_DISMISSED: 'dismissed',
	DISMISSAL_CLOSE: 'close',
	DISMISSAL_VIEW_TEMPLATES: 'viewTemplates',
} as const;

export const FirstSnippetCreatedModal = ({
	onClose,
	onCloseComplete,
}: FirstSnippetCreatedModalProps) => {
	const { Modal } = useChoreographerAssets();
	const { setOnboardingState } = useSetOnboardingState();
	const { setQueryParams } = useRouteActions();
	const pageMode = useGetPageMode();
	const isViewMode = pageMode === PageMode.VIEW;
	const { fireModalClosedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.FIRST_SNIPPET_CREATED_MODAL,
	);

	useEffect(() => {
		fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.FIRST_SNIPPET_CREATED_MODAL, {
			action: FIRST_SNIPPET_CREATED_ANALYTICS.ACTION_VIEWED,
		});
	}, [fireModalClosedAnalytic]);

	const handleDismiss = useCallback(() => {
		fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.FIRST_SNIPPET_CREATED_MODAL, {
			action: FIRST_SNIPPET_CREATED_ANALYTICS.ACTION_DISMISSED,
			dismissalAction: FIRST_SNIPPET_CREATED_ANALYTICS.DISMISSAL_CLOSE,
		});
		setOnboardingState({
			key: SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL,
			value: 'true',
		});
		onClose();
	}, [fireModalClosedAnalytic, setOnboardingState, onClose]);

	const handleViewTemplate = useCallback(() => {
		fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.FIRST_SNIPPET_CREATED_MODAL, {
			action: FIRST_SNIPPET_CREATED_ANALYTICS.ACTION_DISMISSED,
			dismissalAction: FIRST_SNIPPET_CREATED_ANALYTICS.DISMISSAL_VIEW_TEMPLATES,
		});
		setOnboardingState({
			key: SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL,
			value: 'true',
		});
		setInEditorTemplatesPanelQueryParam(
			setQueryParams,
			IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
			CONTEXT_PANEL_TAB_TARGET_BLOCK,
		);
		onClose();
	}, [fireModalClosedAnalytic, setOnboardingState, setQueryParams, onClose]);

	return (
		<Modal onClose={handleDismiss} onCloseComplete={onCloseComplete} width="medium">
			<Box xcss={styles.headerContainer}>
				<Stack space="space.100">
					<Heading size="medium">
						<FormattedMessage {...messages.firstSnippetCreatedModalTitle} />
					</Heading>
					<Text color="color.text.subtle">
						<FormattedMessage {...messages.firstSnippetCreatedModalSubtitle} />
					</Text>
				</Stack>
			</Box>

			<ModalBody>
				<Box xcss={styles.illustrationWrapper}>
					<img
						src={FirstSnippetCreatedIllustration}
						alt=""
						width={554}
						height={253}
						data-testid="first-snippet-created-illustration"
					/>
				</Box>
			</ModalBody>

			<ModalFooter>
				<ButtonGroup>
					{isViewMode ? (
						<Button appearance="primary" onClick={handleDismiss}>
							<FormattedMessage {...messages.firstSnippetCreatedModalOkButton} />
						</Button>
					) : (
						<>
							<Button appearance="subtle" onClick={handleDismiss}>
								<FormattedMessage {...messages.firstSnippetCreatedModalCloseButton} />
							</Button>
							<Button appearance="primary" onClick={handleViewTemplate}>
								<FormattedMessage {...messages.firstSnippetCreatedModalViewButton} />
							</Button>
						</>
					)}
				</ButtonGroup>
			</ModalFooter>
		</Modal>
	);
};
